import { CircularProgress } from "@wealthx/ui";
import { graphql, useStaticQuery } from "gatsby";
import { memo, useEffect, useMemo, useState } from "react";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";

import { BUSINESS_TYPE, STAFF_ROLE, USER_ROLE } from "../../commons/enum";
import { useIsTablet } from "../../hooks/common.hooks";
import {
  useMyStaffInfo,
  useIsSupportRole,
  useIsSupportingCompanyAdmin,
} from "../../hooks/staff.hooks";
import { useMyInfo } from "../../hooks/user.hooks";
import { Account, Dashboard, Globe, LoanCRM, Property, Staff, AIBuilder } from "../../svgs";
import { logout } from "../../utils/auth";
import DesktopSideBar from "./components/desktop-sidebar";
import MobileSideBar from "./components/mobile-menu";
import "./side-bar.scss";
import { useCompanyInfo, useIsAncestorCompanyAdmin } from "../../hooks/company.hooks.ts";

const defaultSideBarMenuItem = [
  {
    icon: <Account />,
    title: "Account",
    href: "account",
  },
  {
    icon: <LocalLibraryOutlinedIcon />,
    title: "Resource Centre",
    href: "resource-center",
  },
  {
    icon: <AIBuilder />,
    title: "WealthX AI",
    href: "ai-builder",
    isCollapsible: true,
    subItems: [
      {
        title: "Conversations",
        href: "ai-builder/conversations",
      },
      {
        title: "Agent List",
        href: "ai-builder/agents",
      },
      {
        title: "Integrations",
        href: "ai-builder/integrations",
      },
      {
        title: "Inbox",
        href: "ai-builder/inbox",
      },
    ],
  },
];

const generateSidebarMenu = (
  userRole,
  isCompanyAdminRole = false,
  isMultiTenantCompany = false,
  currentDomain = "",
  isAncestorCompanyAdmin = false,
  isPlannerCompany = false,
  isAccountantCompany = false,
  isSupportRole = false,
  isSupportingCompanyAdmin = false
) => {
  // Support role supporting a company admin: treat as company admin view
  const effectiveIsCompanyAdmin = isCompanyAdminRole || (isSupportRole && isSupportingCompanyAdmin);

  let sideBar = [...defaultSideBarMenuItem];

  // Hide WealthX AI for Accountant and Planner companies
  if (isAccountantCompany || isPlannerCompany) {
    sideBar = sideBar.filter((item) => item.href !== "ai-builder");
  }

  if (
    (userRole === USER_ROLE.ADMIN && !currentDomain) ||
    (isMultiTenantCompany && (effectiveIsCompanyAdmin || userRole === USER_ROLE.ADMIN))
  ) {
    sideBar = [
      {
        icon: <Property />,
        title: "Company",
        href: "company",
      },
      ...sideBar,
    ];
  }
  if (!isSupportRole && !isPlannerCompany && !isAccountantCompany && currentDomain) {
    sideBar = [
      {
        icon: <LoanCRM />,
        title: "Loan CRM",
        href: "pipeline",
      },
      ...sideBar,
    ];
  }

  if (
    userRole === USER_ROLE.ADMIN ||
    effectiveIsCompanyAdmin ||
    isAncestorCompanyAdmin ||
    isSupportRole
  ) {
    sideBar = [
      {
        icon: <Staff />,
        title: "Staff",
        href: "staff",
      },
      ...sideBar,
    ];
  }
  // * system admin can see webpages of other companies
  // * company admin can see webpages of their own company's hierarchy
  if (
    (!isSupportRole || isSupportingCompanyAdmin) &&
    !isPlannerCompany &&
    !isAccountantCompany &&
    currentDomain &&
    (userRole === USER_ROLE.ADMIN ||
      isCompanyAdminRole ||
      isAncestorCompanyAdmin ||
      (isSupportRole && isSupportingCompanyAdmin))
  ) {
    sideBar = [
      {
        icon: <Globe />,
        title: "Web Pages",
        href: "web-pages",
      },
      ...sideBar,
    ];
  }
  if (userRole !== USER_ROLE.USER) {
    sideBar = [
      {
        icon: <Dashboard />,
        title: "Contact",
        href: "contact",
      },
      ...sideBar,
    ];
  }

  return sideBar;
};

const SideBar = () => {
  const data = useStaticQuery(graphql`
    query {
      gitCommit(latest: { eq: true }) {
        hash
        date
      }
      gitTag(latest: { eq: true }) {
        name
      }
    }
  `);
  const { gitTag, gitCommit: { date: gitDate = "" } = {} } = data;
  const gitTagName = gitTag?.name || "";

  const { data: userInfo } = useMyInfo();
  const { data: staffInfo } = useMyStaffInfo();
  const { data: companyInfo } = useCompanyInfo();
  const isCompanyAdminRole = useMemo(() => {
    return staffInfo?.staffRoles.some(
      (role) => role.role === STAFF_ROLE.COMPANY_ADMIN && role.companyId === companyInfo?.id
    );
  }, [staffInfo, companyInfo]);
  const isPlannerCompany = useMemo(() => {
    return companyInfo?.businessType === BUSINESS_TYPE.PLANNER;
  }, [companyInfo]);
  const isAccountantCompany = useMemo(() => {
    return companyInfo?.businessType === BUSINESS_TYPE.ACCOUNTANT;
  }, [companyInfo]);
  const isMultiTenantCompany = useMemo(() => {
    return companyInfo?.isMultiTenant;
  }, [companyInfo]);

  const isAncestorCompanyAdmin = useIsAncestorCompanyAdmin();

  const isSupportRole = useIsSupportRole();
  const { data: isSupportingCompanyAdminData, isLoading: isSupportLoading } =
    useIsSupportingCompanyAdmin();
  const isSupportingCompanyAdmin = isSupportingCompanyAdminData?.result ?? false;

  const { firstName = "Anonymous", lastName = "", userRole } = userInfo || {};
  const isTablet = useIsTablet();
  const [logoutExpanded, setLogoutExpanded] = useState(false);
  const fullName = `${firstName} ${lastName}`;
  const currentDomain = useMemo(() => {
    return companyInfo?.domain;
  }, [companyInfo]);
  const sideBarMenuItem = useMemo(() => {
    return generateSidebarMenu(
      userRole,
      isCompanyAdminRole,
      isMultiTenantCompany,
      currentDomain,
      isAncestorCompanyAdmin,
      isPlannerCompany,
      isAccountantCompany,
      isSupportRole,
      isSupportingCompanyAdmin
    );
  }, [
    isCompanyAdminRole,
    userRole,
    isMultiTenantCompany,
    currentDomain,
    isAncestorCompanyAdmin,
    isPlannerCompany,
    isAccountantCompany,
    isSupportRole,
    isSupportingCompanyAdmin,
  ]);

  useEffect(() => {
    setLogoutExpanded(false);
  }, [isTablet]);

  const handleLogoutExpanded = () => {
    setLogoutExpanded(!logoutExpanded);
  };
  const handleLogout = () => {
    logout();
  };

  if (isSupportRole && isSupportLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          padding: "20px",
        }}
      >
        <CircularProgress size={24} />
      </div>
    );
  }

  return isTablet ? (
    <MobileSideBar
      sideBarMenuItem={sideBarMenuItem}
      name={fullName}
      handleLogout={handleLogout}
      isExpanded={logoutExpanded}
      handleIsExpanded={handleLogoutExpanded}
      gitVersion={{ gitTagName, gitDate }}
    />
  ) : (
    <DesktopSideBar
      name={fullName}
      sideBarMenuItem={sideBarMenuItem}
      gitVersion={{ gitTagName, gitDate }}
    />
  );
};

export default memo(SideBar);
